import type { RoomSummary } from '@crowdset/contracts';
import { AVATAR_IDS } from '@crowdset/contracts/constants';
import { Avatar } from '@crowdset/ui';
import { MagnifyingGlass, Plus, Shuffle } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { CreateRoomDialog } from '../components/CreateRoomDialog';
import { RoomCard, RoomCardSkeleton } from '../components/RoomCard';
import { toast } from '../components/Toasts';
import { errorMessage } from '../lib/api';
import { fetchRandomRoom, useFavorites, useMe, useRooms, useSession } from '../lib/queries';
import { Button, cx, Dialog, EmptyState, Input } from '../ui/kit';

const SORTS = [
  { value: 'active', label: 'Most active' },
  { value: 'new', label: 'Newest' },
  { value: 'name', label: 'A–Z' },
] as const;

export function Discovery() {
  const me = useMe();
  const session = useSession().data;
  const nav = useNavigate();
  const [draft, setDraft] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<'active' | 'new' | 'name'>('active');
  const rooms = useRooms(q, sort);
  // Filter as you type (debounced) as well as on Enter.
  useEffect(() => {
    const t = setTimeout(() => setQ(draft.trim()), 300);
    return () => clearTimeout(t);
  }, [draft]);
  const favorites = useFavorites(!!me);
  const [creating, setCreating] = useState(false);
  const [randomBusy, setRandomBusy] = useState(false);
  const [fallback, setFallback] = useState<RoomSummary[] | null>(null);

  const all = rooms.data?.pages.flatMap((p) => p.items) ?? [];
  const activeCount = all.filter((r) => r.participantCount > 0).length;

  const joinRandom = async () => {
    setRandomBusy(true);
    try {
      const r = await fetchRandomRoom();
      if (r.room) nav(`/r/${r.room.slug}`);
      else setFallback(r.fallback);
    } catch (e) {
      toast(errorMessage(e), 'error');
    } finally {
      setRandomBusy(false);
    }
  };

  const startCreate = () => {
    if (!me) nav('/signin?returnTo=/');
    else setCreating(true);
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 pb-16">
      {/* Hero: left-aligned copy, character lineup on the right */}
      <section className="grid grid-cols-1 items-end gap-8 py-10 md:grid-cols-[1.1fr_1fr] md:py-14">
        <div className="cs-rise">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.18em] text-accent">
            {rooms.isSuccess
              ? `${activeCount} room${activeCount === 1 ? '' : 's'} with people in them now`
              : 'Rooms'}
          </p>
          <h1 className="max-w-[18ch] text-4xl font-semibold leading-[1.02] tracking-tighter md:text-6xl">
            Take turns. <span className="text-fg-muted">Play the room.</span>
          </h1>
          <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-fg-muted">
            Walk in as a character, queue up for the booth, and play one track when it is your turn.
            Everyone hears roughly the same moment of the same video.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              size="lg"
              onClick={() => void joinRandom()}
              loading={randomBusy}
            >
              <Shuffle size={18} weight="bold" aria-hidden /> Join a random room
            </Button>
            <Button variant="secondary" size="lg" onClick={startCreate}>
              <Plus size={18} weight="bold" aria-hidden /> Create a room
            </Button>
          </div>
          {!me && session && (
            <p className="mt-4 text-sm text-fg-subtle">
              Listening as a guest. Sign in to chat, vote, DJ and save playlists.
            </p>
          )}
        </div>
        <div className="relative hidden h-56 md:block" aria-hidden="true">
          <div className="absolute inset-x-6 bottom-2 h-10 rounded-[50%] bg-accent/[0.07] blur-2xl" />
          <div className="absolute inset-0 flex items-end justify-center gap-0">
            {AVATAR_IDS.map((id, i) => (
              <div
                key={id}
                style={{ transform: `translateY(${[0, 14, 4, 18, 2, 12, 6, 16, 0, 10][i]}px)` }}
                className="-mx-1.5"
              >
                <Avatar
                  avatarId={id}
                  size={62}
                  state={i % 3 === 0 ? 'dance' : 'idle'}
                  phase={i * 0.37}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {me && (favorites.data?.length ?? 0) > 0 && (
        <section aria-labelledby="saved-h" className="mb-10">
          <h2 id="saved-h" className="mb-4 text-lg font-semibold tracking-tight">
            Saved rooms
          </h2>
          <div className="flex snap-x gap-4 overflow-x-auto pb-2 cs-scroll">
            {favorites.data!.map((r, i) => (
              <div key={r.id} className="w-[300px] shrink-0 snap-start">
                <RoomCard room={r} index={i} />
              </div>
            ))}
          </div>
        </section>
      )}

      <section aria-labelledby="browse-h">
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center">
          <h2 id="browse-h" className="text-lg font-semibold tracking-tight md:mr-auto">
            Browse rooms
          </h2>
          <form
            role="search"
            className="relative w-full md:w-80"
            onSubmit={(e) => {
              e.preventDefault();
              setQ(draft.trim());
            }}
          >
            <MagnifyingGlass
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle"
              aria-hidden
            />
            <Input
              aria-label="Search rooms by name or tag"
              placeholder="Search names or tags"
              className="pl-9"
              value={draft}
              maxLength={100}
              onChange={(e) => {
                setDraft(e.target.value);
                if (e.target.value === '') setQ('');
              }}
            />
          </form>
          <div
            role="radiogroup"
            aria-label="Sort rooms"
            className="flex rounded-xl border border-white/[0.08] bg-ink-900 p-1"
          >
            {SORTS.map((s) => (
              <button
                key={s.value}
                role="radio"
                aria-checked={sort === s.value}
                onClick={() => setSort(s.value)}
                className={cx(
                  'h-8 rounded-lg px-3 text-[13px] transition-colors',
                  sort === s.value ? 'bg-ink-700 text-fg' : 'text-fg-muted hover:text-fg',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {rooms.isPending ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <RoomCardSkeleton key={i} />
            ))}
          </div>
        ) : rooms.isError ? (
          <EmptyState
            title="Rooms could not be loaded"
            body={errorMessage(rooms.error)}
            action={<Button onClick={() => void rooms.refetch()}>Try again</Button>}
          />
        ) : all.length === 0 ? (
          <EmptyState
            title={q ? `No rooms match “${q}”` : 'No public rooms yet'}
            body={
              q
                ? 'Try a different name or genre tag.'
                : 'Be the first: create a room and invite a few people.'
            }
            action={
              <Button variant="primary" onClick={startCreate}>
                <Plus size={16} weight="bold" aria-hidden /> Create a room
              </Button>
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {all.map((r, i) => (
                <RoomCard key={r.id} room={r} index={i % 12} />
              ))}
            </div>
            {rooms.hasNextPage && (
              <div className="mt-8 flex justify-center">
                <Button
                  onClick={() => void rooms.fetchNextPage()}
                  loading={rooms.isFetchingNextPage}
                >
                  Load more rooms
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      <CreateRoomDialog open={creating} onOpenChange={setCreating} />

      <Dialog
        open={fallback !== null}
        onOpenChange={(o) => !o && setFallback(null)}
        title="Nobody is in a room right now"
        description="No public room has people in it at the moment. Start one of these quiet rooms — the queue is yours."
      >
        {fallback && fallback.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {fallback.map((r) => (
              <li key={r.id}>
                <button
                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/10 px-4 py-3 text-left hover:border-white/25"
                  onClick={() => nav(`/r/${r.slug}`)}
                >
                  <span>
                    <span className="block text-sm font-medium">{r.name}</span>
                    <span className="text-xs text-fg-muted">
                      {r.genreTags.join(' · ') || 'No tags'}
                    </span>
                  </span>
                  <span className="text-xs text-accent">Open</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            title="No public rooms exist"
            action={
              <Button
                variant="primary"
                onClick={() => {
                  setFallback(null);
                  startCreate();
                }}
              >
                Create a room
              </Button>
            }
          />
        )}
      </Dialog>
    </div>
  );
}
